import React, { useState, useEffect } from 'react';
import { getInstagramFeed, InstagramPost } from '../../utils/InstagramAPI';
import './InstagramFeed.css';

function formatInstagramCount(value: number) {
  if (value < 1000) return String(value);
  const scaled = value / 1000;
  const compact = scaled >= 10 ? String(Math.round(scaled)) : scaled.toFixed(1).replace(/\.0$/, '');
  return `${compact}k`;
}

interface InstagramFeedProps {
  limit?: number;
  showCaptions?: boolean;
  showStats?: boolean;
  columns?: number;
  className?: string;
  onAvailability?: (visible: boolean) => void;
}

const InstagramFeed: React.FC<InstagramFeedProps> = ({
  limit = 12,
  showCaptions = false,
  showStats = false,
  columns,
  className = '',
  onAvailability
}) => {
  const [columnCount, setColumnCount] = useState(columns ?? 4);
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const loadFeed = async () => {
      try {
        setLoading(true);
        const data = await getInstagramFeed();
        if (!active) return;
        const parsedColumns = Number(data.columns);
        const parsedLimit = Number(data.displayLimit);
        const nextColumns = Number.isInteger(parsedColumns) && parsedColumns > 0
          ? parsedColumns
          : (columns ?? 4);
        const nextLimit = Number.isInteger(parsedLimit) && parsedLimit > 0 ? parsedLimit : limit;
        setColumnCount(nextColumns);
        if (Array.isArray(data.posts) && data.posts.length > 0) {
          setPosts(data.posts.slice(0, nextLimit));
          onAvailability?.(true);
        } else {
          setPosts([]);
          onAvailability?.(false);
        }
      } catch (err: any) {
        console.error('Error loading Instagram feed:', err);
        if (!active) return;
        setPosts([]);
        onAvailability?.(false);
      } finally {
        if (active) setLoading(false);
      }
    };
    loadFeed();
    return () => {
      active = false;
    };
  }, [columns, limit, onAvailability]);

  if (loading || posts.length === 0) {
    return null;
  }

  return (
    <div className={`instagram-feed ${className}`}>
      <div
        className="instagram-posts-grid"
        data-columns={columnCount}
      >
        {posts.map((post) => {
          const caption = (post.caption || '').replace(/\s+/g, ' ').trim();
          const likes = typeof post.likeCount === 'number' ? post.likeCount : null;
          const comments = typeof post.commentsCount === 'number' ? post.commentsCount : null;
          const hasEngagement = showStats && (likes !== null || comments !== null);

          return (
            <a
              key={post.id}
              href={post.permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="instagram-post-card"
            >
              <div className="instagram-post-image-container">
                <img
                  src={post.mediaUrl}
                  alt={caption ? '' : 'Instagram post'}
                  className="instagram-post-image"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
                {hasEngagement && (
                  <div className="instagram-post-overlay">
                    <div className="instagram-post-stats">
                      {likes !== null && (
                        <span className="stat-item">
                          <i className="fas fa-heart" aria-hidden="true"></i>
                          <span>{formatInstagramCount(likes)}</span>
                        </span>
                      )}
                      {comments !== null && (
                        <span className="stat-item">
                          <i className="fas fa-comment" aria-hidden="true"></i>
                          <span>{formatInstagramCount(comments)}</span>
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {showCaptions && caption && (
                <div className="instagram-post-caption">
                  <p>{caption}</p>
                </div>
              )}
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default InstagramFeed;

